/**
 * Cliente HTTP do front.
 *
 * A sessão vive num cookie httpOnly emitido pela API — o token nunca passa por
 * JavaScript nem por localStorage, que era como o sistema antigo guardava e é
 * exatamente o que um XSS rouba. Por isso todo fetch vai com
 * `credentials: 'include'` e não existe header Authorization aqui.
 */

import { registrarOrigem } from './pwa.ts';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(caminho: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${caminho}`, {
    ...init,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });
  registrarOrigem(res);

  const tipo = res.headers.get('content-type') ?? '';
  const corpo: unknown = tipo.includes('application/json') ? await res.json() : await res.text();

  if (!res.ok) {
    const doServidor =
      corpo && typeof corpo === 'object' && 'message' in corpo
        ? String((corpo as { message: unknown }).message).trim()
        : '';
    // Erro interno costuma vir com `message` vazio — o banco fora do ar, por
    // exemplo. Mensagem vazia virava um login que "não entra e não dá erro".
    const msg =
      doServidor ||
      (res.status >= 500
        ? 'O servidor não conseguiu responder agora. Tente de novo em instantes.'
        : `Falha na requisição (HTTP ${res.status})`);
    throw new ApiError(msg, res.status);
  }

  return corpo as T;
}

export const api = {
  get: <T>(caminho: string) => request<T>(caminho),
  post: <T>(caminho: string, corpo?: unknown) =>
    request<T>(caminho, { method: 'POST', body: corpo ? JSON.stringify(corpo) : undefined }),
  put: <T>(caminho: string, corpo: unknown) =>
    request<T>(caminho, { method: 'PUT', body: JSON.stringify(corpo) }),
  delete: <T>(caminho: string) => request<T>(caminho, { method: 'DELETE' }),
};
